import { ApiClient, makeAdminApi, type TokenStore, type TokenPair } from '@wheez/api-client';

const STORAGE_KEY = 'wheez_admin_tokens';
export const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

type StoredTokens = TokenPair & { expiresAt?: number };

let refreshTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleProactiveRefresh(tokens: StoredTokens | null) {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = undefined;
  if (!tokens?.refreshToken || !tokens.expiresAt) return;
  const ms = tokens.expiresAt - Date.now() - 2 * 60_000;
  if (ms <= 0) return;
  refreshTimer = setTimeout(async () => {
    const t = await localStorageTokenStore.get();
    if (!t?.refreshToken) return;
    const next = await refreshTokens(t.refreshToken);
    if (next) await localStorageTokenStore.set(next);
  }, ms);
}

async function refreshTokens(refreshToken: string): Promise<StoredTokens | null> {
  const res = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { accessToken: string; refreshToken: string; expiresIn: number };
  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + data.expiresIn * 1000,
  };
}

const localStorageTokenStore: TokenStore = {
  async get() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as StoredTokens) : null;
    } catch {
      return null;
    }
  },
  async set(t) {
    if (t) {
      const stored: StoredTokens = {
        ...t,
        expiresAt: (t as StoredTokens).expiresAt ?? Date.now() + 8 * 3600_000,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      scheduleProactiveRefresh(stored);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      scheduleProactiveRefresh(null);
    }
  },
};

export const client = new ApiClient({
  baseUrl: API_BASE_URL,
  tokenStore: localStorageTokenStore,
  refresh: (refreshToken) => refreshTokens(refreshToken),
  onUnauthenticated: () => {
    if (location.pathname !== '/login') location.assign('/login');
  },
});

export const adminApi = makeAdminApi(client);
export const tokenStore = localStorageTokenStore;

export async function isAuthenticated() {
  const t = (await tokenStore.get()) as StoredTokens | null;
  if (!t?.accessToken) return false;
  if (t.expiresAt && t.expiresAt > Date.now() + 60_000) return true;
  if (!t.refreshToken) return false;
  const next = await refreshTokens(t.refreshToken);
  if (!next) return false;
  await tokenStore.set(next);
  return true;
}

void tokenStore.get().then((t) => scheduleProactiveRefresh(t as StoredTokens | null));
